import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
import { cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "../cn.js";
import { Spinner } from "./Spinner.js";
export const buttonVariants = cva([
    // Labels are set in the mono face (Geist Mono), whose own line box (line-height: normal)
    // centres the capitals. An inherited line height or a padding offset put them up to 1px high,
    // differently in Chrome and in WebKit (the Mac app's engine); this is within 0.25px in both.
    "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-mono font-medium [line-height:normal] tracking-[0.01em] whitespace-nowrap select-none",
    "transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
], {
    variants: {
        variant: {
            // Linear's indigo, with a faint top highlight.
            primary: "bg-primary text-primary-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(0_0_0/0.12)] hover:bg-primary-hover",
            secondary: "border border-border bg-overlay text-fg shadow-xs hover:bg-surface-muted",
            ghost: "text-fg-muted hover:bg-surface-muted hover:text-fg",
            danger: "bg-danger text-danger-fg shadow-xs hover:bg-danger/90",
            // Text that acts: inline in a sentence or a row ("Change", "Remove").
            link: "!h-auto !px-0 !pb-0 font-sans tracking-normal text-accent hover:underline hover:underline-offset-[3px]",
        },
        size: {
            // One text size for every button, the same as the fields'.
            sm: "h-7 gap-1 px-2.5 text-[13px]",
            md: "h-8 px-3.5 text-[13px]",
            lg: "h-9 px-4 text-[13px]",
        },
    },
    defaultVariants: { variant: "primary", size: "md" },
});
export function Button({ className, variant, size, asChild = false, loading = false, disabled, type, children, ref, ...props }) {
    const classes = cn(buttonVariants({ variant, size }), className);
    if (asChild) {
        return (_jsx(Slot.Root, { ref: ref, className: classes, ...props, children: children }));
    }
    return (_jsxs("button", { ref: ref, 
        // Default to "button" so buttons inside forms never submit by accident.
        type: type ?? "button", className: classes, disabled: disabled === true || loading, "aria-busy": loading || undefined, ...props, children: [loading && _jsx(Spinner, { size: size === "lg" ? "md" : "sm" }), children] }));
}
/** Square button that shows only an icon. The label becomes its accessible name. */
export function IconButton({ label, icon, size = "md", variant = "ghost", className, ...props }) {
    const square = { sm: "w-7 px-0 pb-0", md: "w-8 px-0 pb-0", lg: "w-9 px-0 pb-0" }[size ?? "md"];
    return (_jsx(Button, { "aria-label": label, size: size, variant: variant, className: cn(square, className), ...props, children: _jsx("span", { "aria-hidden": "true", className: "inline-flex", children: icon }) }));
}
//# sourceMappingURL=Button.js.map